export const CATEGORY_BRP_ENTRY_COOKIE = 'gt_lp_entry_brp';
export const SERVICES_LANDING_PATH = '/business-services';

/** 浏览器 session 内暂存 Header/MHC Services 首跳标记。 */
export const SERVICES_BRP_HOTSPOT_SESSION_KEY = 'gt_services_brp_hotspot';
const PENDING_SERVICES_BRP_HOTSPOT = 'pending';
const SERVICES_BRP_HOTSPOT_HISTORY_KEY = 'servicesBrpHotspot';

export type CategoryBrpEntry =
    | 'cars:mobile'
    | 'cars:desktop'
    | 'services:mobile'
    | 'services:desktop';

const isServicesBrpEntry = (entry: CategoryBrpEntry) =>
    entry === 'services:mobile' || entry === 'services:desktop';

const setServicesBrpHotspotSession = (value?: string) => {
    if (typeof sessionStorage === 'undefined') {
        return;
    }

    try {
        if (value) {
            sessionStorage.setItem(SERVICES_BRP_HOTSPOT_SESSION_KEY, value);
            return;
        }

        sessionStorage.removeItem(SERVICES_BRP_HOTSPOT_SESSION_KEY);
    } catch {
        // Safari private mode 等场景下 sessionStorage 可能不可用
    }
};

const getServicesBrpHotspotSession = () => {
    if (typeof sessionStorage === 'undefined') {
        return undefined;
    }

    try {
        return sessionStorage.getItem(SERVICES_BRP_HOTSPOT_SESSION_KEY) || undefined;
    } catch {
        return undefined;
    }
};

const hasServicesBrpHotspotHistoryEntry = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    const state = window.history.state;
    return Boolean(
        state &&
            typeof state === 'object' &&
            (state as Record<string, unknown>)[SERVICES_BRP_HOTSPOT_HISTORY_KEY] === true
    );
};

const setServicesBrpHotspotHistoryEntry = () => {
    if (typeof window === 'undefined') {
        return;
    }

    const state =
        window.history.state && typeof window.history.state === 'object'
            ? window.history.state
            : {};

    window.history.replaceState(
        { ...state, [SERVICES_BRP_HOTSPOT_HISTORY_KEY]: true },
        '',
        window.location.href
    );
};

export const setCategoryBrpEntryCookie = (entry: CategoryBrpEntry) => {
    if (typeof document === 'undefined') {
        return;
    }

    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${CATEGORY_BRP_ENTRY_COOKIE}=${entry}; Max-Age=60; Path=/; SameSite=Lax${secure}`;

    // Services 入口先写 pending，SRP 首次加载时再绑定到实际的 Header 目标 URL。
    setServicesBrpHotspotSession(
        isServicesBrpEntry(entry) ? PENDING_SERVICES_BRP_HOTSPOT : undefined
    );
};

/** Header / MHC 大 Services 入口会写入；Category 筛选跳转不会。用于热点模块门控。 */
export const hasServicesBrpEntryCookie = (): boolean => {
    if (typeof document === 'undefined') {
        return false;
    }

    return /(?:^|; )gt_lp_entry_brp=services:(?:mobile|desktop)(?:;|$)/.test(document.cookie);
};

/**
 * Header/MHC 首跳会标记当前浏览器 history entry。
 * 热点分类或筛选器打开的新页面不带该标记；浏览器返回原入口时才会重新展示热点。
 */
export const hasServicesBrpHotspotEntry = (): boolean => {
    if (hasServicesBrpHotspotHistoryEntry()) {
        return true;
    }

    const storedValue = getServicesBrpHotspotSession();
    if (hasServicesBrpEntryCookie() || storedValue === PENDING_SERVICES_BRP_HOTSPOT) {
        setServicesBrpHotspotHistoryEntry();
        setServicesBrpHotspotSession();
        return true;
    }

    return false;
};

export const clearCategoryBrpEntryCookie = () => {
    if (typeof document === 'undefined') {
        return;
    }

    document.cookie = `${CATEGORY_BRP_ENTRY_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
};

export const clearServicesBrpHotspotEntry = () => {
    if (typeof window !== 'undefined') {
        const state =
            window.history.state && typeof window.history.state === 'object'
                ? { ...window.history.state }
                : {};

        delete state[SERVICES_BRP_HOTSPOT_HISTORY_KEY];
        window.history.replaceState(state, '', window.location.href);
    }

    clearCategoryBrpEntryCookie();
    setServicesBrpHotspotSession();
};

/** 清除当前 tab 内 Header/MHC Services 热点入口记录。 */
export const clearServicesBrpHotspotSession = () => {
    setServicesBrpHotspotSession();
};
